import { useCallback, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnimatePresence } from 'motion/react';

// Components
import { ICON_SUPPORT_LINE } from '~/components/icon';
import SuspendedPlayerModalContent from '~/components/payment/suspended-players-modal/content';

// Hooks
import useTranslations from '~/components/translation/use-translations';
import { useSuspendedPlayerModal } from '~/components/payment/suspended-players-modal/hook/useSuspendedPlayerModal';
import { useCloseCurrentContext } from '~/hooks/use-close-context';

// Actions
import { showSupportDialogBox } from '~/components/support/ducks';
import { selectSuspendedPlayerModalData } from '~/components/account/ducks';

// Constants
import {
	OPERATION_TYPES,
	SUSPENDED_REASONS,
	TEST_IDS,
	UI_TEXT,
	TEXT_KEYS
} from '~/components/payment/suspended-players-modal/constants';

// Styled components
import { ModalWrapper } from '~/components/payment/suspended-players-modal/content/styles';
import { updateAdaCustomMetafield } from '~/components/support/ducks/index-ada';

interface SuspendedPlayerModalProps {
	center?: boolean;
	qdLimitsFlow?: boolean;
}

const SuspendedPlayerModal: FC<SuspendedPlayerModalProps> = ({ qdLimitsFlow = false, center = false }) => {
	const dispatch = useDispatch();
	const modalData = useSelector(selectSuspendedPlayerModalData);
	const { isLoadingTranslations, translations: t } = useTranslations(TEXT_KEYS);
	const { hideSuspendedModal } = useSuspendedPlayerModal();
	const closeCurrentContext = useCloseCurrentContext();

	const operationType = modalData?.operationType || OPERATION_TYPES.DEPOSIT;
	const title = t[UI_TEXT.TITLES[operationType]];
	const content = t[UI_TEXT.CONTENT[operationType]];

	const handleClose = useCallback(() => {
		if (qdLimitsFlow) {
			closeCurrentContext();
		}

		hideSuspendedModal();
	}, [closeCurrentContext, hideSuspendedModal, qdLimitsFlow]);

	const openSupport = useCallback(() => {
		dispatch(updateAdaCustomMetafield('proactiveChatReason', SUSPENDED_REASONS[operationType]));

		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		dispatch(showSupportDialogBox(true));
	}, [dispatch, operationType]);

	return (
		<ModalWrapper show={!isLoadingTranslations} blurBg data-testid={TEST_IDS.SUPPORT_BUTTON}>
			<AnimatePresence mode="wait">
				<SuspendedPlayerModalContent
					center={center}
					onClose={handleClose}
					title={title}
					content={content}
					primaryAction={{
						text: t[UI_TEXT.BUTTON.CONTACT_SUPPORT],
						onClick: openSupport,
						iconType: ICON_SUPPORT_LINE,
						className: 'btn-support',
						testId: TEST_IDS.SUPPORT_BUTTON
					}}
				/>
			</AnimatePresence>
		</ModalWrapper>
	);
};

export default SuspendedPlayerModal;
